/**
 * InstallPWAPrompt -- Phase 2 Agent D2.
 *
 * Light-weight "Add to Home Screen" nudge:
 *
 *   - On Chromium / Edge / Android the browser fires
 *     `beforeinstallprompt`. We capture the event, suppress the native
 *     bar, and surface our own button. Clicking "Install" calls
 *     `event.prompt()` and resolves with the user's choice. Dismissals
 *     remember themselves in localStorage so we don't nag.
 *   - On iOS Safari there is no programmatic prompt -- we show a brief
 *     instructional banner ("Tap Share, then Add to Home Screen") the
 *     first time the page loads on iPhone/iPad and remember the
 *     dismissal. The banner never appears once the app is already in
 *     standalone mode.
 *   - Inside Capacitor or after install (`display-mode: standalone`)
 *     the component renders nothing.
 *
 * The component is intentionally self-contained: no i18n bundle key
 * footprint, no Tailwind dependency, no Auth coupling. It can be
 * mounted anywhere in the tree -- main.tsx mounts it next to <App />.
 */
import { useEffect, useState } from 'react'


type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}


const DISMISS_KEY = 'orkstra:pwa-install-dismissed-at'
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000  // 14 days


function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true
  // iOS Safari uses the legacy navigator.standalone flag.
  const nav = window.navigator as Navigator & { standalone?: boolean }
  if (nav.standalone === true) return true
  return false
}


function isInCapacitor(): boolean {
  if (typeof window === 'undefined') return false
  const w = window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }
  return Boolean(w.Capacitor?.isNativePlatform?.())
}


function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  return /iPad|iPhone|iPod/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)
}


function isRecentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const ts = Number(raw)
    if (!Number.isFinite(ts)) return false
    return Date.now() - ts < DISMISS_TTL_MS
  } catch {
    return false
  }
}


function markDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
  } catch {
    // localStorage unavailable (Safari private mode); accept the nag.
  }
}


export function InstallPWAPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [showIOSHint, setShowIOSHint] = useState(false)

  useEffect(() => {
    if (isStandalone() || isInCapacitor()) return
    if (isRecentlyDismissed()) return

    const onBeforeInstall = (e: Event) => {
      e.preventDefault()
      setDeferred(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)

    const onInstalled = () => {
      setDeferred(null)
      markDismissed()
    }
    window.addEventListener('appinstalled', onInstalled)

    // iOS path: no programmatic event, just check UA.
    if (isIOS()) setShowIOSHint(true)

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const dismiss = () => {
    markDismissed()
    setDeferred(null)
    setShowIOSHint(false)
  }

  const install = async () => {
    if (!deferred) return
    try {
      await deferred.prompt()
      const choice = await deferred.userChoice
      if (choice.outcome === 'accepted') {
        markDismissed()
      }
    } catch {
      // fall through -- closing the prompt is fine
    } finally {
      setDeferred(null)
    }
  }

  if (!deferred && !showIOSHint) return null

  const isAR = typeof document !== 'undefined'
    && document.documentElement.lang === 'ar'

  const label = deferred
    ? (isAR ? 'تثبيت ORKSTRA على الشاشة الرئيسية' : 'Install ORKSTRA on your home screen')
    : (isAR
        ? 'لتثبيت ORKSTRA: اضغط على زر المشاركة ثم "إضافة إلى الشاشة الرئيسية".'
        : 'To install ORKSTRA: tap the Share button, then "Add to Home Screen".')
  const installText = isAR ? 'تثبيت' : 'Install'
  const dismissText = isAR ? 'ليس الآن' : 'Not now'

  return (
    <div
      role="dialog"
      aria-label="install-pwa"
      data-testid="install-pwa-prompt"
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        maxWidth: '28rem',
        width: 'calc(100% - 1.5rem)',
        background: '#1E40AF',
        color: 'white',
        padding: '0.85rem 1rem',
        borderRadius: '0.75rem',
        boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.75rem',
        fontSize: '0.9rem',
        direction: isAR ? 'rtl' : 'ltr',
      }}
    >
      <span style={{ flex: 1, lineHeight: 1.35 }}>{label}</span>

      {deferred ? (
        <button
          type="button"
          onClick={install}
          data-testid="install-pwa-accept"
          style={{
            background: 'white',
            color: '#1E40AF',
            border: 'none',
            padding: '0.45rem 0.9rem',
            borderRadius: '0.5rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {installText}
        </button>
      ) : null}

      <button
        type="button"
        onClick={dismiss}
        data-testid="install-pwa-dismiss"
        aria-label={dismissText}
        style={{
          background: 'transparent',
          color: 'white',
          border: '1px solid rgba(255,255,255,0.5)',
          padding: '0.45rem 0.8rem',
          borderRadius: '0.5rem',
          cursor: 'pointer',
        }}
      >
        {dismissText}
      </button>
    </div>
  )
}

export default InstallPWAPrompt
